import { TFunction } from 'i18next';
import { OperatorResult, QcOperator } from '90.quickConnect.Models/enums';
import { AllFieldValueTypes, Choice } from '90.quickConnect.Models/models';

/* Helpers */
const escapeForRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const normalizeText = (v: unknown): string => String(v ?? '').trim();

/**
 * Choice - Choice (avec les labels normalisés)
 */
export const operationWithChoices = (
  ref1Value: AllFieldValueTypes,
  ref2Value: AllFieldValueTypes,
  type: QcOperator,
  t: TFunction,
): [AllFieldValueTypes, OperatorResult] => {
  const ref1Label = normalizeText((ref1Value as Choice)?.label);
  const ref2Label = normalizeText((ref2Value as Choice)?.label);
  switch (type) {
    case QcOperator.Add: {
      const out = [ref1Label, ref2Label].filter((s) => s.length > 0).join(', ');
      return [out, OperatorResult.STRING];
    }
    case QcOperator.Subst: {
      if (!ref2Label) return [ref1Label, OperatorResult.STRING];
      const out = ref1Label
        .replace(new RegExp(escapeForRegExp(ref2Label), 'gi'), '') // insensible à la casse
        .replace(/\s{2,}/g, ' ')
        .trim();
      return [out, OperatorResult.STRING];
    }
    case QcOperator.Mult:
    case QcOperator.Div:
      return [t('computed_fields_errors_operator_incompatible').toString(), OperatorResult.ERROR];
    default:
      return [t('computed_fields_errors_operator_type_unknown').toString(), OperatorResult.ERROR];
  }
};

/**
 * Choice[] - Choice[] (concat/soustraction par égalité de label normalisé)
 */
export const operationWithChoicesArray = (
  ref1Value: AllFieldValueTypes,
  ref2Value: AllFieldValueTypes,
  type: QcOperator,
  t: TFunction,
): [AllFieldValueTypes, OperatorResult] => {
  const ref1Choices: Choice[] =
    ref1Value == null ? [] : Array.isArray(ref1Value) ? (ref1Value as Choice[]) : [ref1Value as Choice];
  const ref2Choices: Choice[] =
    ref2Value == null ? [] : Array.isArray(ref2Value) ? (ref2Value as Choice[]) : [ref2Value as Choice];

  const label = (c?: Choice) => normalizeText(c?.label);

  switch (type) {
    case QcOperator.Add: {
      const out = [...ref1Choices, ...ref2Choices]
        .map(label)
        .filter((s) => s.length > 0)
        .join(', ');
      return [out, OperatorResult.STRING];
    }
    case QcOperator.Subst: {
      const outChoices = ref1Choices.filter((x) => !ref2Choices.some((y) => label(x) === label(y)));
      const out = outChoices
        .map(label)
        .filter((s) => s.length > 0)
        .join(', ');
      return [out, OperatorResult.STRING];
    }
    case QcOperator.Mult:
    case QcOperator.Div:
    default:
      return [t('computed_fields_errors_operator_incompatible').toString(), OperatorResult.ERROR];
  }
};

/**
 * string - (Choice | Choice[])
 * Add : concat propre avec ", "
 * Subst : suppression insensible à la casse
 */
export const operateStringWithChoices = (
  ref1Value: AllFieldValueTypes,
  ref2Value: AllFieldValueTypes,
  type: QcOperator,
  t: TFunction,
): [AllFieldValueTypes, OperatorResult] => {
  const ref1IsString = typeof ref1Value === 'string';
  const ref2IsString = typeof ref2Value === 'string';

  // Cas simples : string vs undefined -> renvoie la string normalisée
  if (ref1IsString && ref2Value == null) return [normalizeText(ref1Value), OperatorResult.STRING];
  if (ref2IsString && ref1Value == null) return [normalizeText(ref2Value), OperatorResult.STRING];

  // On traite uniquement les cas mixtes string ↔ (Choice|Choice[])
  const ref1IsChoiceLike =
    ref1Value != null && (Array.isArray(ref1Value) || (typeof ref1Value === 'object' && 'label' in (ref1Value as any)));
  const ref2IsChoiceLike =
    ref2Value != null && (Array.isArray(ref2Value) || (typeof ref2Value === 'object' && 'label' in (ref2Value as any)));
  if (!((ref1IsString && ref2IsChoiceLike) || (ref2IsString && ref1IsChoiceLike))) {
    return [t('computed_fields_errors_operator_incompatible').toString(), OperatorResult.ERROR];
  }

  const ref1String = ref1IsString ? normalizeText(ref1Value) : '';
  const ref2String = ref2IsString ? normalizeText(ref2Value) : '';

  const ref1Choices: Choice[] = ref1IsString
    ? []
    : Array.isArray(ref1Value)
    ? (ref1Value as Choice[])
    : [ref1Value as Choice];
  const ref2Choices: Choice[] = ref2IsString
    ? []
    : Array.isArray(ref2Value)
    ? (ref2Value as Choice[])
    : [ref2Value as Choice];

  const label = (c?: Choice) => normalizeText(c?.label);

  switch (type) {
    case QcOperator.Add: {
      if (ref1IsString) {
        const rightLabels = ref2Choices
          .map(label)
          .filter((s) => s.length > 0)
          .join(', ');
        const out = [ref1String, rightLabels].filter((s) => s.length > 0).join(', ');
        return [out, OperatorResult.STRING];
      } else {
        const leftLabels = ref1Choices
          .map(label)
          .filter((s) => s.length > 0)
          .join(', ');
        const out = [leftLabels, ref2String].filter((s) => s.length > 0).join(', ');
        return [out, OperatorResult.STRING];
      }
    }

    case QcOperator.Subst: {
      if (ref2IsString) {
        // Retirer ref2String de chaque label des ref1Choices
        const toRemove = ref2String;
        const cleaned = (
          toRemove
            ? ref1Choices.map((c) => label(c).replace(new RegExp(escapeForRegExp(toRemove), 'gi'), ''))
            : ref1Choices.map(label)
        ).filter((s) => s.length > 0);
        return [cleaned.join(', '), OperatorResult.STRING];
      } else {
        // Retirer tous les labels de ref2Choices du ref1String (regex unique)
        const labels = ref2Choices.map(label).filter((s) => s.length > 0);
        if (labels.length === 0) return [ref1String, OperatorResult.STRING];

        labels.sort((a, b) => b.length - a.length); // limite les chevauchements
        const pattern = new RegExp(`(${labels.map(escapeForRegExp).join('|')})`, 'gi');

        const out = ref1String
          .replace(pattern, '')
          .replace(/\s{2,}/g, ' ')
          .trim();
        return [out, OperatorResult.STRING];
      }
    }

    case QcOperator.Mult:
    case QcOperator.Div:
    default:
      return [t('computed_fields_errors_operator_incompatible').toString(), OperatorResult.ERROR];
  }
};

/**
 * Détermine si les éléments de la liste en parametre sont présents dans la liste courante
 * si andExclusif est true, tous les éléments doivent etre trouvés
 * si andExclusif est false, au moins 1 element doit etre trouvé
 *
 * @param cl
 * @return
 */
export const containsValues = (
  choices: Choice[],
  strings: string[],
  andExclusif: boolean,
  isEqualsLength = false,
): boolean => {
  if (strings.length === 0) return false;

  // On vérfie l'égalité de la longueur des 2 tableaux si isEqualsLength est à TRUE
  if (andExclusif && isEqualsLength && choices.length !== strings.length) return false;

  // Dans le cas ou on est dans l'opérateur EQUALS et qu'on une liste de chaines de caractères...
  if (!andExclusif && isEqualsLength && choices.length !== 1) return false;

  // On vérifie selon le andExcusif avec l'opérateur "every" ou "some"
  return andExclusif
    ? strings.every((str: string) => choices.map(({ value }: Choice) => value ?? '').includes(str))
    : strings.some((str: string) => choices.map(({ value }: Choice) => value ?? '').includes(str));
};
